import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Users, BookOpen, FileText, MessageCircle,
  TrendingUp, CheckCircle, Clock, Sparkles, CreditCard, 
  RefreshCw, ChevronRight, Send, Mail, X, Eye, Megaphone,
  User, Calendar, PenLine, Search, AlertTriangle, Ban, Loader2,
  BarChart3, Activity, Zap, Globe, Award, Trash2, RotateCcw,
  ChevronDown, ChevronUp, Mic, Download
} from 'lucide-react';
import DarkModeToggle from '@/components/DarkModeToggle';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== Mini Chart Component ====================
function MiniBarChart({ data, dataKey, color = '#6366f1', height = 60 }) {
  if (!data || data.length === 0) return <div className="h-[60px] flex items-center justify-center text-xs text-gray-400">No data</div>;
  const max = Math.max(...data.map(d => d[dataKey] || 0), 1);
  const barWidth = Math.max(4, Math.floor((100 / data.length) * 0.7));
  const gap = Math.max(1, Math.floor((100 / data.length) * 0.3));

  return (
    <div className="flex items-end justify-between" style={{ height }} data-testid="mini-bar-chart">
      {data.map((d, i) => {
        const val = d[dataKey] || 0;
        const h = Math.max(2, (val / max) * height);
        return (
          <div key={i} className="group relative flex flex-col items-center" style={{ width: `${barWidth}%`, marginRight: i < data.length - 1 ? `${gap}%` : 0 }}>
            <div className="absolute -top-6 hidden group-hover:block bg-gray-900 text-white text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">
              {val} &middot; {d.date?.slice(5)}
            </div>
            <div className="w-full rounded-t-sm transition-all duration-200 hover:opacity-80" style={{ height: h, backgroundColor: color, minWidth: 3 }} />
          </div>
        );
      })}
    </div>
  );
}

// ==================== Donut Chart Component ====================
function DonutChart({ data, colors }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <div className="text-center text-sm text-gray-400">No data</div>;
  let cumulative = 0;
  const size = 120;
  const stroke = 20;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="flex items-center gap-4" data-testid="donut-chart">
      <svg width={size} height={size} className="transform -rotate-90">
        {data.map((d, i) => {
          const pct = d.value / total;
          const offset = cumulative * circumference;
          cumulative += pct;
          return (
            <circle key={i} cx={size / 2} cy={size / 2} r={radius} fill="none"
              stroke={colors[i % colors.length]} strokeWidth={stroke}
              strokeDasharray={`${pct * circumference} ${circumference}`}
              strokeDashoffset={-offset} className="transition-all duration-500" />
          );
        })}
      </svg>
      <div className="space-y-1.5">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[i % colors.length] }} />
            <span className="text-gray-600 dark:text-gray-300">{d.label}</span>
            <span className="font-semibold text-gray-900 dark:text-white">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== Stat Card ====================
function StatCard({ icon: Icon, label, value, subtext, color, onClick }) {
  const colors = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    stone: 'bg-stone-100 dark:bg-stone-800/20 text-stone-600 dark:text-stone-400',
    pink: 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
    rose: 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400',
  };

  return (
    <div 
      onClick={onClick}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
      className={`bg-white dark:bg-gray-800 rounded-xl p-4 border border-[#E8DCC4] dark:border-gray-700 ${onClick ? 'cursor-pointer hover:shadow-lg hover:border-stone-400 transition-all' : ''}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color] || colors.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
        {onClick && <ChevronRight className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="text-2xl font-bold text-[#1A1A1A] dark:text-[#F5F1E8]">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
      {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
    </div>
  );
}

// ==================== Activity Event ====================
function ActivityEvent({ event }) {
  const icons = { signup: User, book_created: BookOpen, feedback: MessageCircle };
  const colors = { signup: 'text-green-500', book_created: 'text-purple-500', feedback: 'text-pink-500' };
  const Icon = icons[event.type] || Activity;
  const color = colors[event.type] || 'text-gray-500';
  const timeAgo = getTimeAgo(event.timestamp);

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
      <div className={`mt-0.5 ${color}`}><Icon className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 dark:text-gray-200">
          <span className="font-medium">{event.user_name}</span>{' '}
          <span className="text-gray-500 dark:text-gray-400">{event.detail}</span>
        </p>
        <p className="text-xs text-gray-400 mt-0.5">{timeAgo}</p>
      </div>
    </div>
  );
}

function getTimeAgo(timestamp) {
  if (!timestamp) return '';
  const now = new Date();
  const then = new Date(timestamp);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
}

// ==================== Main Admin Dashboard ====================
function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [books, setBooks] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [couponData, setCouponData] = useState(null);
  
  // Advanced analytics
  const [growthData, setGrowthData] = useState(null);
  const [activeUsersData, setActiveUsersData] = useState(null);
  const [aiUsageData, setAiUsageData] = useState(null);
  const [genreData, setGenreData] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [contentStats, setContentStats] = useState(null);
  const [pageViewData, setPageViewData] = useState(null);
  
  // UI states
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSort, setUserSort] = useState('newest');
  const [bookSort, setBookSort] = useState('newest');
  
  // Message form
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Announcement form
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementBody, setAnnouncementBody] = useState('');
  const [announcementTarget, setAnnouncementTarget] = useState('all');
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [analyticsRes, usersRes, booksRes, feedbackRes, announcementsRes, couponRes] = await Promise.allSettled([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/books`),
        axios.get(`${API}/feedback/all`),
        axios.get(`${API}/admin/announcements`),
        axios.get(`${API}/coupon-report/test101`),
      ]);

      if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data);
      else console.error('analytics/overview failed:', analyticsRes.reason);

      if (usersRes.status === 'fulfilled') setUsers(Array.isArray(usersRes.value.data) ? usersRes.value.data : []);
      else toast.error('Failed to load users');

      if (booksRes.status === 'fulfilled') setBooks(Array.isArray(booksRes.value.data) ? booksRes.value.data : []);
      else toast.error('Failed to load books');

      if (feedbackRes.status === 'fulfilled') setFeedbacks(Array.isArray(feedbackRes.value.data) ? feedbackRes.value.data : []);
      else console.error('feedback/all failed:', feedbackRes.reason);

      if (announcementsRes.status === 'fulfilled') setAnnouncements(Array.isArray(announcementsRes.value.data) ? announcementsRes.value.data : []);
      else console.error('announcements failed:', announcementsRes.reason);

      setCouponData(couponRes.status === 'fulfilled' ? couponRes.value.data : null);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAnalyticsData = useCallback(async () => {
    try {
      const [growthRes, activeRes, aiRes, genreRes, topRes, feedRes, contentRes, pageViewRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/growth?days=30`).catch(() => null),
        axios.get(`${API}/admin/analytics/active-users`).catch(() => null),
        axios.get(`${API}/admin/analytics/ai-usage?days=30`).catch(() => null),
        axios.get(`${API}/admin/analytics/genres`).catch(() => null),
        axios.get(`${API}/admin/analytics/top-users?limit=10`).catch(() => null),
        axios.get(`${API}/admin/analytics/activity-feed?limit=20`).catch(() => null),
        axios.get(`${API}/admin/analytics/content-stats`).catch(() => null),
        axios.get(`${API}/admin/analytics/page-views?days=14`).catch(() => null),
      ]);
      setGrowthData(growthRes?.data || null);
      setActiveUsersData(activeRes?.data || null);
      setAiUsageData(aiRes?.data || null);
      setGenreData(Array.isArray(genreRes?.data) ? genreRes.data : []);
      setTopUsers(Array.isArray(topRes?.data) ? topRes.data : []);
      setActivityFeed(Array.isArray(feedRes?.data) ? feedRes.data : []);
      setContentStats(contentRes?.data || null);
      setPageViewData(pageViewRes?.data || null);
    } catch (e) {
      console.error('Analytics load error:', e);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadAnalyticsData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadData(), loadAnalyticsData()]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const loadUserDetails = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`);
      setUserDetails(res.data);
      setSelectedUser(userId);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleSuspendUser = async (userId, currentStatus, userEmail) => {
    const ADMIN_EMAILS = ["appthestoryteller@gmail.com", "yaronmadmon@gmail.com"];
    if (ADMIN_EMAILS.includes(userEmail)) { toast.error('Cannot suspend admin users'); return; }
    if (!window.confirm(`${currentStatus ? 'Unsuspend' : 'Suspend'} this user?`)) return;
    try {
      setActionLoading(prev => ({ ...prev, [userId]: 'suspend' }));
      const endpoint = currentStatus ? `${API}/admin/users/${userId}/unsuspend` : `${API}/admin/users/${userId}/suspend`;
      await axios.put(endpoint, {});
      toast.success(currentStatus ? 'User unsuspended' : 'User suspended');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    const ADMIN_EMAILS = ["appthestoryteller@gmail.com", "yaronmadmon@gmail.com"];
    if (ADMIN_EMAILS.includes(userEmail)) { toast.error('Cannot delete admin users'); return; }
    if (!window.confirm('Permanently delete this user and ALL their data? This cannot be undone.')) return;
    if (!window.confirm('Are you absolutely sure? Type the action to confirm.')) return;
    try {
      setActionLoading(prev => ({ ...prev, [userId]: 'delete' }));
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      setSelectedUser(null);
      setUserDetails(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleResetUsage = async (userId) => {
    if (!window.confirm('Reset this user\'s monthly usage limits?')) return;
    try {
      setActionLoading(prev => ({ ...prev, [userId]: 'reset' }));
      await axios.post(`${API}/admin/users/${userId}/reset-usage`);
      toast.success('Usage limits reset');
      if (selectedUser === userId) loadUserDetails(userId);
    } catch (error) {
      toast.error('Failed to reset usage');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleChangePlan = async (userId, plan) => {
    if (!window.confirm(`Change this user's plan to "${plan}"?`)) return;
    try {
      setActionLoading(prev => ({ ...prev, [userId]: 'plan' }));
      await axios.put(`${API}/admin/users/${userId}/plan`, { plan });
      toast.success(`Plan changed to ${plan}`);
      if (selectedUser === userId) loadUserDetails(userId);
      await loadData();
    } catch (error) {
      toast.error('Failed to change plan');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const sendMessage = async () => {
    if (!messageSubject.trim() || !messageBody.trim()) { toast.error('Please fill in subject and message'); return; }
    setSendingMessage(true);
    try {
      await axios.post(`${API}/admin/message`, { user_id: selectedUser, subject: messageSubject, message: messageBody });
      toast.success('Message sent!');
      setShowMessageModal(false);
      setMessageSubject(''); setMessageBody('');
      loadUserDetails(selectedUser);
    } catch (error) { toast.error('Failed to send message'); }
    finally { setSendingMessage(false); }
  };

  const sendAnnouncement = async () => {
    if (!announcementTitle.trim() || !announcementBody.trim()) { toast.error('Please fill in title and message'); return; }
    setSendingAnnouncement(true);
    try {
      await axios.post(`${API}/admin/announcement`, { title: announcementTitle, message: announcementBody, target: announcementTarget });
      toast.success('Announcement sent!');
      setShowAnnouncementModal(false);
      setAnnouncementTitle(''); setAnnouncementBody('');
      loadData();
    } catch (error) { toast.error('Failed to send announcement'); }
    finally { setSendingAnnouncement(false); }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try { await axios.delete(`${API}/admin/announcement/${id}`); toast.success('Deleted'); loadData(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const updateFeedbackStatus = async (feedbackId, status) => {
    try {
      await axios.put(`${API}/feedback/${feedbackId}/status?status=${status}`, {});
      setFeedbacks(feedbacks.map(f => f.feedback_id === feedbackId ? { ...f, status } : f));
      toast.success('Status updated');
    } catch (error) { toast.error('Failed to update status'); }
  };

  // Sorting
  const sortedUsers = [...(Array.isArray(users) ? users : [])].filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => {
    if (userSort === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (userSort === 'words') return (b.total_words || 0) - (a.total_words || 0);
    if (userSort === 'books') return (b.books_count || 0) - (a.books_count || 0);
    if (userSort === 'name') return (a.name || '').localeCompare(b.name || '');
    return 0;
  });

  const sortedBooks = [...(Array.isArray(books) ? books : [])].filter(b =>
    b.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.author_name?.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => {
    if (bookSort === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (bookSort === 'words') return (b.total_words || 0) - (a.total_words || 0);
    if (bookSort === 'title') return (a.title || '').localeCompare(b.title || '');
    return 0;
  });

  const filteredFeedbacks = Array.isArray(feedbacks) ? feedbacks.filter(fb =>
    fb.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    fb.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    fb.type?.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1E8] dark:bg-[#1A1A1A]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2D2D2D] dark:border-[#F5F1E8] mx-auto mb-4"></div>
          <p className="text-sm text-gray-500">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'users', label: `Users (${users.length})`, icon: Users },
    { id: 'books', label: `Books (${books.length})`, icon: BookOpen },
    { id: 'feedback', label: `Feedback (${feedbacks.filter(f => f.status === 'new').length} new)`, icon: MessageCircle },
    { id: 'coupons', label: 'Coupons', icon: CreditCard },
    { id: 'announcements', label: 'Announce', icon: Megaphone },
  ];

  return (
    <div className="min-h-screen bg-[#F5F1E8] dark:bg-[#1A1A1A] mobile-page" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-800/80 border-b border-[#E8DCC4] dark:border-gray-700 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="p-2" data-testid="admin-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-base sm:text-xl font-bold text-[#1A1A1A] dark:text-[#F5F1E8]">Admin Dashboard</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Full platform management & analytics</p>
              </div>
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <Button onClick={() => setShowAnnouncementModal(true)} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white px-2 sm:px-4" data-testid="announce-btn">
                <Megaphone className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Announce</span>
              </Button>
              <Button onClick={handleRefresh} variant="outline" size="sm" disabled={refreshing} className="px-2 sm:px-3" data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <DarkModeToggle />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Search */}
        <div className="mb-4 sm:mb-6">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="admin-global-search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users, books, feedback..." className="pl-10 pr-10 py-2 text-sm rounded-full border border-[#E8DCC4] dark:border-gray-600 bg-white dark:bg-gray-800 w-full" />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 sm:space-x-2 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-hide">
          {tabs.map(tab => (
            <Button key={tab.id} onClick={() => { setActiveTab(tab.id); setSelectedUser(null); }}
              variant={activeTab === tab.id ? 'default' : 'outline'} size="sm"
              data-testid={`tab-${tab.id}`}
              className={`flex-shrink-0 text-xs sm:text-sm px-2 sm:px-4 ${activeTab === tab.id ? 'bg-[#2D2D2D] text-white dark:bg-gray-600' : ''}`}>
              <tab.icon className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden ml-1">{tab.id === 'overview' ? 'Home' : tab.id === 'analytics' ? 'Stats' : tab.id === 'feedback' ? 'FB' : tab.id === 'announcements' ? 'News' : tab.label.split(' ')[0]}</span>
            </Button>
          ))}
        </div>

        {/* ==================== OVERVIEW TAB ==================== */}
        {activeTab === 'overview' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Primary Stats */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
              <StatCard icon={Users} label="Total Users" value={analytics?.totals?.users || 0}
                subtext={`+${analytics?.this_week?.new_users || 0} this week`} color="blue" onClick={() => setActiveTab('users')} />
              <StatCard icon={BookOpen} label="Total Books" value={analytics?.totals?.books || 0}
                subtext={`+${analytics?.this_week?.new_books || 0} this week`} color="purple" onClick={() => setActiveTab('books')} />
              <StatCard icon={FileText} label="Total Words" value={contentStats?.words?.total || 0}
                subtext={`${contentStats?.words?.avg_per_chapter || 0} avg/chapter`} color="stone" />
              <StatCard icon={MessageCircle} label="New Feedback" value={analytics?.feedback?.new || 0}
                subtext={`${analytics?.feedback?.total || 0} total`} color="pink" onClick={() => setActiveTab('feedback')} />
            </div>

            {/* Second row */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
              <StatCard icon={Activity} label="DAU" value={activeUsersData?.dau || 0}
                subtext={`${activeUsersData?.dau_percent || 0}% of users`} color="green" />
              <StatCard icon={Activity} label="WAU" value={activeUsersData?.wau || 0}
                subtext={`${activeUsersData?.wau_percent || 0}% of users`} color="amber" />
              <StatCard icon={Activity} label="MAU" value={activeUsersData?.mau || 0}
                subtext={`${activeUsersData?.mau_percent || 0}% of users`} color="indigo" />
              <StatCard icon={Sparkles} label="Interview Sessions" value={contentStats?.interviews?.total_sessions || 0}
                subtext={`${contentStats?.interviews?.completed || 0} completed`} color="rose" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Activity Feed */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                <h2 className="text-base font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                  <Activity className="w-4 h-4 mr-2 text-indigo-500" /> Recent Activity
                </h2>
                <div className="max-h-[340px] overflow-y-auto pr-1">
                  {activityFeed.length > 0 ? activityFeed.slice(0, 12).map((e, i) => <ActivityEvent key={i} event={e} />) :
                    <p className="text-sm text-gray-400 text-center py-6">No recent activity</p>}
                </div>
              </div>

              {/* Subscriptions + Genre */}
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                  <h2 className="text-base font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                    <CreditCard className="w-4 h-4 mr-2 text-green-500" /> Subscriptions
                  </h2>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">{analytics?.subscriptions?.free || 0}</div>
                      <div className="text-xs text-gray-500">Free</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{analytics?.subscriptions?.premium || 0}</div>
                      <div className="text-xs text-blue-600">Premium</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{analytics?.subscriptions?.pro || 0}</div>
                      <div className="text-xs text-purple-600">Pro</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                  <h2 className="text-base font-semibold mb-3 flex items-center text-gray-900 dark:text-white">
                    <BookOpen className="w-4 h-4 mr-2 text-purple-500" /> Genre Distribution
                  </h2>
                  <DonutChart data={genreData.slice(0, 6).map(g => ({ label: g.genre, value: g.count }))}
                    colors={['#6366f1', '#a855f7', '#ec4899', '#f59e0b', '#10b981', '#3b82f6']} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== ANALYTICS TAB ==================== */}
        {activeTab === 'analytics' && (
          <div className="space-y-4 sm:space-y-6">
            {/* User Growth Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
              <h2 className="text-base font-semibold mb-1 text-gray-900 dark:text-white flex items-center">
                <TrendingUp className="w-4 h-4 mr-2 text-blue-500" /> User Growth
                <span className="ml-auto text-xs text-gray-400 font-normal">Last 30 days</span>
              </h2>
              <p className="text-xs text-gray-400 mb-4">New signups per day</p>
              <MiniBarChart data={growthData?.data} dataKey="users" color="#3b82f6" height={80} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Book Growth */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                <h2 className="text-base font-semibold mb-1 text-gray-900 dark:text-white flex items-center">
                  <BookOpen className="w-4 h-4 mr-2 text-purple-500" /> Books Created
                  <span className="ml-auto text-xs text-gray-400 font-normal">30d</span>
                </h2>
                <MiniBarChart data={growthData?.data} dataKey="books" color="#a855f7" height={70} />
              </div>

              {/* Chapter Growth */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                <h2 className="text-base font-semibold mb-1 text-gray-900 dark:text-white flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-stone-500" /> Chapters Written
                  <span className="ml-auto text-xs text-gray-400 font-normal">30d</span>
                </h2>
                <MiniBarChart data={growthData?.data} dataKey="chapters" color="#78716c" height={70} />
              </div>
            </div>

            {/* Daily Active Users Trend */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
              <h2 className="text-base font-semibold mb-1 text-gray-900 dark:text-white flex items-center">
                <Users className="w-4 h-4 mr-2 text-green-500" /> Daily Active Users
                <span className="ml-auto text-xs text-gray-400 font-normal">14d trend</span>
              </h2>
              <div className="grid grid-cols-3 gap-4 mb-4 mt-3">
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-xl font-bold text-green-600">{activeUsersData?.dau || 0}</div>
                  <div className="text-xs text-green-600">DAU ({activeUsersData?.dau_percent || 0}%)</div>
                </div>
                <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="text-xl font-bold text-amber-600">{activeUsersData?.wau || 0}</div>
                  <div className="text-xs text-amber-600">WAU ({activeUsersData?.wau_percent || 0}%)</div>
                </div>
                <div className="text-center p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                  <div className="text-xl font-bold text-indigo-600">{activeUsersData?.mau || 0}</div>
                  <div className="text-xs text-indigo-600">MAU ({activeUsersData?.mau_percent || 0}%)</div>
                </div>
              </div>
              <MiniBarChart data={activeUsersData?.daily_trend} dataKey="active" color="#10b981" height={70} />
            </div>

            {/* AI Usage Breakdown */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
              <h2 className="text-base font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
                <Zap className="w-4 h-4 mr-2 text-amber-500" /> AI Feature Usage
                <span className="ml-auto text-xs text-gray-400 font-normal">Last 30 days</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                {[
                  { label: 'Polish', value: aiUsageData?.totals?.polish || 0, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: PenLine },
                  { label: 'Ignite', value: aiUsageData?.totals?.ignite || 0, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', icon: Sparkles },
                  { label: 'Interview', value: aiUsageData?.totals?.interview || 0, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', icon: Mic },
                  { label: 'TTS', value: aiUsageData?.totals?.tts || 0, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: Globe },
                  { label: 'Total', value: aiUsageData?.totals?.total || 0, color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: Activity },
                ].map((item) => (
                  <div key={item.label} className={`text-center p-3 rounded-lg ${item.color}`}>
                    <item.icon className="w-4 h-4 mx-auto mb-1 opacity-70" />
                    <div className="text-lg font-bold">{item.value.toLocaleString()}</div>
                    <div className="text-xs">{item.label}</div>
                  </div>
                ))}
              </div>
              <MiniBarChart data={aiUsageData?.daily} dataKey="total" color="#f59e0b" height={60} />
            </div>

            {/* Content Stats + Top Users */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Content Stats */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                <h2 className="text-base font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-stone-500" /> Content Overview
                </h2>
                <div className="space-y-3">
                  {[
                    { label: 'Total Books', value: contentStats?.books?.total || 0, sub: `${contentStats?.books?.interview || 0} interview, ${contentStats?.books?.manual || 0} manual` },
                    { label: 'Total Chapters', value: contentStats?.chapters?.total || 0, sub: `${contentStats?.chapters?.avg_per_book || 0} avg per book` },
                    { label: 'Total Words', value: (contentStats?.words?.total || 0).toLocaleString(), sub: `${(contentStats?.words?.avg_per_chapter || 0).toLocaleString()} avg per chapter` },
                    { label: 'Exports', value: contentStats?.exports?.total_exports || 0, sub: `${contentStats?.exports?.books_exported || 0} books exported` },
                    { label: 'Interview Sessions', value: contentStats?.interviews?.total_sessions || 0, sub: `${contentStats?.interviews?.completed || 0} completed` },
                  ].map(row => (
                    <div key={row.label} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                      <div><div className="text-sm font-medium text-gray-700 dark:text-gray-200">{row.label}</div><div className="text-xs text-gray-400">{row.sub}</div></div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{row.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Users */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                <h2 className="text-base font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
                  <Award className="w-4 h-4 mr-2 text-amber-500" /> Top Users
                </h2>
                <div className="space-y-2.5">
                  {topUsers.slice(0, 8).map((u, i) => (
                    <div key={u.user_id} className="flex items-center gap-3 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-lg px-2 -mx-2"
                      onClick={() => { loadUserDetails(u.user_id); setActiveTab('users'); }}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-200 text-gray-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'
                      }`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{u.name}</div>
                        <div className="text-xs text-gray-400">{u.total_words.toLocaleString()} words &middot; {u.books_count} books</div>
                      </div>
                      <div className="text-xs text-gray-400">{u.ai_calls_month} AI</div>
                    </div>
                  ))}
                  {topUsers.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No data yet</p>}
                </div>
              </div>
            </div>

            {/* Page Views */}
            {pageViewData && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                <h2 className="text-base font-semibold mb-1 text-gray-900 dark:text-white flex items-center">
                  <Globe className="w-4 h-4 mr-2 text-cyan-500" /> Page Views (last 14 days)
                </h2>
                <div className="flex gap-6 mb-3">
                  <div><div className="text-2xl font-bold text-gray-900 dark:text-white">{(pageViewData.total_views || 0).toLocaleString()}</div><div className="text-xs text-gray-400">Total views</div></div>
                  <div><div className="text-2xl font-bold text-gray-900 dark:text-white">{(pageViewData.total_unique_users || 0).toLocaleString()}</div><div className="text-xs text-gray-400">Unique users (sum)</div></div>
                </div>
                <MiniBarChart data={pageViewData.daily} dataKey="views" color="#06b6d4" height={60} />
                {pageViewData.top_pages?.length > 0 && (
                  <div className="mt-4 space-y-1.5">
                    {pageViewData.top_pages.slice(0, 5).map(p => (
                      <div key={p.page} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-300 truncate max-w-[70%]">{p.page}</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{p.views}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ==================== USERS TAB ==================== */}
        {activeTab === 'users' && !selectedUser && (
          <div className="space-y-4">
            {/* Sort controls */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Sort:</span>
              {['newest', 'words', 'books', 'name'].map(s => (
                <button key={s} onClick={() => setUserSort(s)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${userSort === s ? 'bg-[#2D2D2D] text-white border-transparent' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            {/* Desktop table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-[#E8DCC4] dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Plan</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Books</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Words</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Last Active</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedUsers.length > 0 ? sortedUsers.map(user => (
                      <tr key={user.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => loadUserDetails(user.user_id)}>
                            <div className="w-8 h-8 bg-gradient-to-br from-stone-500 to-stone-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                              {user.name?.[0] || 'U'}
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 dark:text-white truncate">{user.name}</div>
                              <div className="text-xs text-gray-500 truncate">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className={`px-2 py-1 text-xs rounded-full ${user.plan === 'pro' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : user.plan === 'premium' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>
                            {user.plan}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hidden sm:table-cell">{user.books_count}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hidden md:table-cell">{user.total_words?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">
                          {user.last_activity ? new Date(user.last_activity).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          {user.is_suspended ? (
                            <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"><Ban className="w-3 h-3 mr-1" />Suspended</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"><CheckCircle className="w-3 h-3 mr-1" />Active</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-1">
                            <Button size="sm" variant="ghost" onClick={() => loadUserDetails(user.user_id)} className="px-2" data-testid={`view-user-${user.user_id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost"
                              onClick={() => handleSuspendUser(user.user_id, user.is_suspended, user.email)}
                              disabled={!!actionLoading[user.user_id] || user.email === 'appthestoryteller@gmail.com'}
                              className={`px-2 ${user.is_suspended ? 'text-green-600' : 'text-red-500'}`}>
                              {actionLoading[user.user_id] === 'suspend' ? <Loader2 className="w-4 h-4 animate-spin" /> : user.is_suspended ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">{searchQuery ? `No users matching "${searchQuery}"` : 'No users yet'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ==================== USER DETAILS ==================== */}
        {activeTab === 'users' && selectedUser && userDetails && (
          <div className="space-y-4 sm:space-y-6">
            <Button variant="ghost" onClick={() => setSelectedUser(null)} data-testid="back-to-users"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Users</Button>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 sm:p-6 border border-[#E8DCC4] dark:border-gray-700">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-stone-500 to-stone-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {userDetails.user?.name?.[0] || 'U'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{userDetails.user?.name}</h2>
                    <p className="text-sm text-gray-500">{userDetails.user?.email}</p>
                    <div className="flex items-center space-x-2 mt-1.5">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${userDetails.subscription?.plan === 'pro' ? 'bg-purple-100 text-purple-700' : userDetails.subscription?.plan === 'premium' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                        {userDetails.subscription?.plan || 'free'}
                      </span>
                      <span className="text-xs text-gray-400">Joined {new Date(userDetails.user?.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setShowMessageModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-message-btn">
                    <Mail className="w-4 h-4 mr-1" /> Message
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleResetUsage(selectedUser)} disabled={!!actionLoading[selectedUser]} data-testid="reset-usage-btn">
                    {actionLoading[selectedUser] === 'reset' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />} Reset Usage
                  </Button>
                  <div className="flex items-center gap-1.5" data-testid="change-plan-controls">
                    <select
                      defaultValue={userDetails.subscription?.plan || 'free'}
                      onChange={e => handleChangePlan(selectedUser, e.target.value)}
                      disabled={!!actionLoading[selectedUser]}
                      className="h-8 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs px-2 text-gray-700 dark:text-gray-200"
                      data-testid="change-plan-select"
                    >
                      <option value="free">Free</option>
                      <option value="premium">Premium</option>
                      <option value="pro">Pro</option>
                    </select>
                    {actionLoading[selectedUser] === 'plan' && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                  </div>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => handleDeleteUser(selectedUser, userDetails.user?.email)} disabled={!!actionLoading[selectedUser]}
                    data-testid="delete-user-btn">
                    {actionLoading[selectedUser] === 'delete' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Delete
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3 mt-5">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-xl font-bold text-gray-900 dark:text-white">{userDetails.stats?.books_count}</div>
                  <div className="text-xs text-gray-500">Books</div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-xl font-bold text-gray-900 dark:text-white">{userDetails.stats?.total_words?.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">Words</div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-xl font-bold text-gray-900 dark:text-white">{userDetails.stats?.ai_calls_this_month}</div>
                  <div className="text-xs text-gray-500">AI Calls/mo</div>
                </div>
              </div>
            </div>

            {/* User's Books */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
              <h3 className="text-base font-semibold mb-3 flex items-center"><BookOpen className="w-4 h-4 mr-2 text-purple-500" /> Books ({userDetails.books?.length || 0})</h3>
              <div className="space-y-2">
                {userDetails.books?.map(book => (
                  <div key={book.book_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div><div className="font-medium text-sm">{book.title}</div><div className="text-xs text-gray-500">{book.genre} &middot; {book.chapters_count} ch</div></div>
                    <span className="text-xs text-gray-400">{new Date(book.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
                {(!userDetails.books || userDetails.books.length === 0) && <p className="text-gray-400 text-center text-sm py-3">No books</p>}
              </div>
            </div>

            {/* Messages + Feedback */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                <h3 className="text-base font-semibold mb-3 flex items-center"><Mail className="w-4 h-4 mr-2 text-blue-500" /> Messages ({userDetails.messages?.length || 0})</h3>
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {userDetails.messages?.map(msg => (
                    <div key={msg.message_id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium text-sm">{msg.subject}</div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${msg.read ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{msg.read ? 'Read' : 'Unread'}</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">{msg.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                  {(!userDetails.messages || userDetails.messages.length === 0) && <p className="text-gray-400 text-center text-sm py-3">No messages</p>}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                <h3 className="text-base font-semibold mb-3 flex items-center"><MessageCircle className="w-4 h-4 mr-2 text-pink-500" /> Feedback ({userDetails.feedbacks?.length || 0})</h3>
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {userDetails.feedbacks?.map(fb => (
                    <div key={fb.feedback_id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium capitalize">{fb.type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${fb.status === 'resolved' ? 'bg-green-100 text-green-700' : fb.status === 'reviewed' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{fb.status}</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2">{fb.message}</p>
                    </div>
                  ))}
                  {(!userDetails.feedbacks || userDetails.feedbacks.length === 0) && <p className="text-gray-400 text-center text-sm py-3">No feedback</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== BOOKS TAB ==================== */}
        {activeTab === 'books' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Sort:</span>
              {['newest', 'words', 'title'].map(s => (
                <button key={s} onClick={() => setBookSort(s)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${bookSort === s ? 'bg-[#2D2D2D] text-white border-transparent' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-[#E8DCC4] dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="books-table">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Book</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Author</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Genre</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ch</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Words</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedBooks.length > 0 ? sortedBooks.map(book => (
                      <tr key={book.book_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3"><div className="font-medium text-gray-900 dark:text-white text-sm">{book.title}</div></td>
                        <td className="px-4 py-3 hidden sm:table-cell"><div className="text-sm text-gray-600 dark:text-gray-300">{book.author_name}</div><div className="text-xs text-gray-400">{book.author_email}</div></td>
                        <td className="px-4 py-3 hidden md:table-cell"><span className="px-2 py-1 text-xs bg-[#E8DCC4] dark:bg-gray-600 rounded-full">{book.genre}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{book.chapters_count}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">{book.total_words?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-gray-400 hidden lg:table-cell">{new Date(book.created_at).toLocaleDateString()}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">{searchQuery ? `No books matching "${searchQuery}"` : 'No books yet'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ==================== FEEDBACK TAB ==================== */}
        {activeTab === 'feedback' && (
          <div className="space-y-3" data-testid="feedback-tab">
            {filteredFeedbacks.length > 0 ? filteredFeedbacks.map(fb => (
              <div key={fb.feedback_id} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-[#E8DCC4] dark:border-gray-700">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">{fb.type === 'love' ? '\u2764\uFE0F' : fb.type === 'feature' ? '\uD83D\uDCA1' : fb.type === 'bug' ? '\uD83D\uDC1B' : '\uD83D\uDCAC'}</span>
                    <div>
                      <div className="font-medium text-sm">{fb.user_name || fb.user_email}</div>
                      <div className="text-xs text-gray-400">{new Date(fb.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${fb.status === 'resolved' ? 'bg-green-100 text-green-700' : fb.status === 'reviewed' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{fb.status}</span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{fb.message}</p>
                <div className="flex flex-wrap gap-2">
                  {fb.status === 'new' && (
                    <Button size="sm" variant="outline" onClick={() => updateFeedbackStatus(fb.feedback_id, 'reviewed')}><Clock className="w-3 h-3 mr-1" /> Reviewed</Button>
                  )}
                  {fb.status !== 'resolved' && (
                    <Button size="sm" variant="outline" className="text-green-600" onClick={() => updateFeedbackStatus(fb.feedback_id, 'resolved')}><CheckCircle className="w-3 h-3 mr-1" /> Resolve</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => { loadUserDetails(fb.user_id); setActiveTab('users'); }}><User className="w-3 h-3 mr-1" /> View User</Button>
                </div>
              </div>
            )) : <div className="text-center py-12 text-gray-500">{searchQuery ? `No feedback matching "${searchQuery}"` : 'No feedback yet'}</div>}
          </div>
        )}

        {/* ==================== COUPONS TAB ==================== */}
        {activeTab === 'coupons' && (
          <div className="space-y-4">
            {couponData ? (
              <>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-[#E8DCC4] dark:border-gray-700">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-[#1A1A1A] dark:text-[#F5F1E8] mb-1">TEST101 Coupon</h2>
                      <p className="text-sm text-gray-500">{couponData.summary?.status} &middot; {couponData.summary?.message}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-purple-600">{couponData.users_count}</div>
                      <div className="text-xs text-gray-500">Active Users</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-gray-900 dark:text-white">{couponData.total_uses}</div>
                      <div className="text-xs text-gray-500">Total Uses</div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-purple-600">{couponData.is_active ? 'Active' : 'Inactive'}</div>
                      <div className="text-xs text-purple-600">Status</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-[#E8DCC4] dark:border-gray-700 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50">
                    <h3 className="font-semibold text-sm flex items-center"><Users className="w-4 h-4 mr-2 text-purple-500" /> Users ({couponData.users?.length || 0})</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">User</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 hidden sm:table-cell">Email</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tier</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 hidden sm:table-cell">Applied</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {couponData.users?.map((user, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="px-4 py-2 text-sm font-medium">{user.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-500 hidden sm:table-cell">{user.email}</td>
                            <td className="px-4 py-2"><span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">{user.tier_granted}</span></td>
                            <td className="px-4 py-2 text-xs text-gray-400 hidden sm:table-cell">{new Date(user.applied_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(!couponData.users || couponData.users.length === 0) && <div className="p-6 text-center text-gray-500 text-sm">No users redeemed</div>}
                </div>
              </>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-[#E8DCC4] dark:border-gray-700 text-center">
                <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-500 text-sm">Coupon data not available</p>
              </div>
            )}
          </div>
        )}

        {/* ==================== ANNOUNCEMENTS TAB ==================== */}
        {activeTab === 'announcements' && (
          <div className="space-y-4" data-testid="announcements-tab">
            <Button onClick={() => setShowAnnouncementModal(true)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="new-announcement-btn">
              <Megaphone className="w-4 h-4 mr-2" /> New Announcement
            </Button>
            {announcements.map(ann => (
              <div key={ann.announcement_id} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-[#E8DCC4] dark:border-gray-700">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold">{ann.title}</h3>
                    <p className="text-xs text-gray-500">By {ann.from_admin_name} &middot; {new Date(ann.created_at).toLocaleString()} &middot; Target: {ann.target}</p>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteAnnouncement(ann.announcement_id)}><X className="w-4 h-4" /></Button>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{ann.message}</p>
              </div>
            ))}
            {announcements.length === 0 && <div className="text-center py-12 text-gray-500">No announcements yet</div>}
          </div>
        )}
      </div>

      {/* ==================== MODALS ==================== */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="message-modal">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Send Message</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Subject</label>
                <Input value={messageSubject} onChange={(e) => setMessageSubject(e.target.value)} placeholder="Message subject..." data-testid="message-subject" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <Textarea value={messageBody} onChange={(e) => setMessageBody(e.target.value)} placeholder="Write your message..." rows={4} data-testid="message-body" />
              </div>
              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowMessageModal(false)}>Cancel</Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={sendMessage} disabled={sendingMessage} data-testid="send-message-submit">
                  {sendingMessage ? 'Sending...' : 'Send Message'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="announcement-modal">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Send Announcement</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <Input value={announcementTitle} onChange={(e) => setAnnouncementTitle(e.target.value)} placeholder="Announcement title..." data-testid="announcement-title" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Target Audience</label>
                <select value={announcementTarget} onChange={(e) => setAnnouncementTarget(e.target.value)}
                  data-testid="announcement-target"
                  className="w-full px-3 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 text-sm">
                  <option value="all">All Users</option>
                  <option value="free">Free Users Only</option>
                  <option value="premium">Premium Users Only</option>
                  <option value="pro">Pro Users Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <Textarea value={announcementBody} onChange={(e) => setAnnouncementBody(e.target.value)} placeholder="Write your announcement..." rows={4} data-testid="announcement-body" />
              </div>
              <div className="flex space-x-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowAnnouncementModal(false)}>Cancel</Button>
                <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={sendAnnouncement} disabled={sendingAnnouncement} data-testid="send-announcement-submit">
                  {sendingAnnouncement ? 'Sending...' : 'Send Announcement'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboardPage;
